/**
 * Мини-карта поля (карточка v3, вкладка «Обзор», PR-B).
 *
 * Переключатель Контур | NDVI + до 3 дат последних снимков — по эталону founder
 * (2026-07-11). НАМЕРЕННО без MapLibre:
 *   - «Контур» — SVG-обводка контура поля (обобщение InspectionMiniMap: те же
 *     чистые проекции geojsonToRings / projectMiniMap / ringToPath), ноль
 *     тайлов/сети, нет teardown-граблей GL при размонтировании вкладки.
 *   - «NDVI» — готовый PNG per-field из GET /ndvi/perfield-png/{id}?date=…,
 *     обрезанный по контуру (растр рендерит domain-satellite; мы лишь
 *     показываем картинку).
 *
 * Цвета — semantic-токены (контур через currentColor text-primary); hex нет.
 *
 * AK-459 — пропорции поля в обоих режимах географически верные и одинаковые:
 *   - «Контур»: projectMiniMap применяет аспект-коррекцию cos(φ_center)
 *     (contain + letterbox в квадратном viewBox; см. inspectionLogic.ts).
 *   - «NDVI»: PNG рендерится backend'ом по bbox в EPSG:3857 с сохранением
 *     аспекта (rasters/perfield.py::_perfield_image_dims); Mercator конформна,
 *     локально аспект = земному. `object-contain` сохраняет его при показе.
 */
import { useMemo, useState } from 'react';
import { ImageOff, Layers, Loader2 } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { API_URL } from '@/lib/api';
import {
  geojsonToRings,
  projectMiniMap,
  ringToPath,
} from '../../inspections/inspectionLogic';
import { useFieldContourGeo, useNdviLatest, type ContourGeo } from './widgetApi';
import { formatShortDate } from './widgetLogic';

// Приоритет контура для мини-карты: эталон → рабочий → legacy-фолбэк → любой.
// Значения совпадают с бэкендом (fields_detail.py: FIELD_CONTOUR_TYPES =
// reference|working|irrigated|bogara + синтетический LEGACY_CONTOUR_TYPE).
export const CONTOUR_PRIORITY = ['reference', 'working', 'legacy'] as const;

export function pickGeojson(contours?: ContourGeo[] | null) {
  if (!contours?.length) return null;
  const withGeo = contours.filter((c) => c.geojson?.coordinates);
  if (!withGeo.length) return null;
  for (const type of CONTOUR_PRIORITY) {
    const hit = withGeo.find((c) => c.contour_type === type);
    if (hit) return hit.geojson ?? null;
  }
  return withGeo[0].geojson ?? null;
}

type Layer = 'contour' | 'ndvi';

function ContourSvg({
  geojson,
}: {
  geojson?: { type?: string; coordinates?: unknown } | null;
}) {
  const proj = useMemo(() => {
    const rings = geojsonToRings(geojson);
    return projectMiniMap(rings);
  }, [geojson]);

  if (!proj) {
    return (
      <div className="flex h-full w-full flex-col items-center justify-center gap-2 text-muted-foreground">
        <Layers className="h-6 w-6" aria-hidden />
        <span className="text-xs">Контур не задан</span>
      </div>
    );
  }
  return (
    <svg
      viewBox={`0 0 ${proj.size} ${proj.size}`}
      className="h-full w-full text-primary"
      role="img"
      aria-label="Контур поля"
      preserveAspectRatio="xMidYMid meet"
    >
      {proj.ringsXY.map((ring, i) => (
        <path
          key={i}
          d={ringToPath(ring)}
          fill="currentColor"
          fillOpacity={0.14}
          stroke="currentColor"
          strokeWidth={1.5}
          strokeLinejoin="round"
          vectorEffect="non-scaling-stroke"
        />
      ))}
    </svg>
  );
}

export function FieldMiniMap({ fieldId }: { fieldId: string }) {
  const [layer, setLayer] = useState<Layer>('contour');
  const [ndviIdx, setNdviIdx] = useState(0);

  const contours = useFieldContourGeo(fieldId);
  const ndvi = useNdviLatest(fieldId, 3);

  const geojson = useMemo(() => pickGeojson(contours.data), [contours.data]);
  const snapshots = ndvi.data?.dates ?? [];
  const hasNdvi = snapshots.length > 0;
  const active = snapshots[Math.min(ndviIdx, snapshots.length - 1)];

  return (
    <Card className="overflow-hidden">
      {/* Переключатель слоёв = шапка карточки. Паддинги ровно те же, что у
          CardHeader соседних карточек (p-4 pb-3) и строка ровно 24px (h-6) —
          поэтому верх полотна карты совпадает по горизонтали с верхом первой
          строки «Паспорта» в правой панели (баг выравнивания 2026-07-29). */}
      <div className="flex items-center gap-1 p-4 pb-3">
        <button
          type="button"
          onClick={() => setLayer('contour')}
          aria-pressed={layer === 'contour'}
          className={cn(
            'flex h-6 items-center rounded-md px-3 text-xs font-medium transition-colors',
            layer === 'contour'
              ? 'bg-primary/15 text-primary'
              : 'text-muted-foreground hover:bg-accent',
          )}
        >
          Контур
        </button>
        <button
          type="button"
          onClick={() => hasNdvi && setLayer('ndvi')}
          aria-pressed={layer === 'ndvi'}
          disabled={!hasNdvi && !ndvi.isLoading}
          title={!hasNdvi && !ndvi.isLoading ? 'Снимков пока нет' : undefined}
          className={cn(
            'flex h-6 items-center rounded-md px-3 text-xs font-medium transition-colors',
            layer === 'ndvi'
              ? 'bg-primary/15 text-primary'
              : 'text-muted-foreground hover:bg-accent',
            !hasNdvi && !ndvi.isLoading && 'cursor-not-allowed opacity-40 hover:bg-transparent',
          )}
        >
          NDVI
        </button>
      </div>

      {/* Полотно. AK-459: aspect-[3/2] с капом высоты (ориентир —
          FieldGeometryTab aspect/max-h) — на ultra-wide левая колонка широкая,
          и без max-h полотно росло безразмерно. При срабатывании капа контейнер
          становится шире 3/2 — контент letterbox'ится (SVG `meet` /
          object-contain), пропорции поля не искажаются. */}
      <div className="relative aspect-[3/2] max-h-[28rem] min-h-[14rem] w-full bg-muted/40">
        {layer === 'contour' ? (
          contours.isLoading ? (
            <Skeleton className="h-full w-full" />
          ) : (
            <ContourSvg geojson={geojson} />
          )
        ) : ndvi.isLoading ? (
          <div className="flex h-full w-full items-center justify-center text-muted-foreground">
            <Loader2 className="h-6 w-6 animate-spin" aria-hidden />
          </div>
        ) : active ? (
          <img
            key={active.date}
            src={`${API_URL}${active.png_url}`}
            alt={`NDVI поля на ${formatShortDate(active.date)}`}
            className="h-full w-full object-contain"
            loading="lazy"
          />
        ) : (
          <div className="flex h-full w-full flex-col items-center justify-center gap-2 text-muted-foreground">
            <ImageOff className="h-6 w-6" aria-hidden />
            <span className="text-xs">Снимков пока нет</span>
          </div>
        )}

        {/* Даты снимков — только в NDVI-режиме, когда есть что выбирать */}
        {layer === 'ndvi' && hasNdvi && (
          <div className="absolute bottom-2 left-2 flex gap-1">
            {snapshots.map((s, i) => (
              <button
                key={s.date}
                type="button"
                onClick={() => setNdviIdx(i)}
                aria-pressed={i === ndviIdx}
                className={cn(
                  'flex h-6 items-center rounded-md border px-2 text-xs font-medium tabular-nums transition-colors',
                  i === ndviIdx
                    ? 'border-primary bg-card text-primary'
                    : 'border-border bg-card/90 text-muted-foreground hover:text-foreground',
                )}
              >
                {formatShortDate(s.date)}
              </button>
            ))}
          </div>
        )}
      </div>
    </Card>
  );
}

export default FieldMiniMap;
